import { createElement } from "react";
import { toast } from "@/hooks/use-toast";
import { ToastAction, type ToastActionElement } from "@/components/ui/toast";
import { appNavigate } from "@/lib/navigation/appNavigate";
import {
  resolveFriendlyError,
  type ApiErrorLike,
  type ErrorSeverity,
  type FriendlyError,
} from "./errorCatalog";
import { audienceOf, getCurrentUser, type CurrentUserSnapshot } from "@/lib/auth/currentUser";

type ToastVariant = "default" | "destructive" | "success" | "warning" | "info";

function variantForSeverity(severity: ErrorSeverity | undefined): ToastVariant {
  switch (severity) {
    case "warning":
      return "warning";
    case "info":
      return "info";
    default:
      return "destructive";
  }
}

// The catalog's { label, to } fix-it link as a toast button — hidden when the
// user is already on that page.
export function catalogActionButton(action: FriendlyError["action"]): ToastActionElement | undefined {
  if (!action) return undefined;
  if (typeof window !== "undefined" && window.location.pathname === action.to) return undefined;
  return createElement(
    ToastAction,
    // ml-12 lines the button up with the text when it stacks on phones.
    { altText: action.label, className: "ml-12 sm:ml-0", onClick: () => appNavigate(action.to) },
    action.label,
  ) as unknown as ToastActionElement;
}

export interface ShowApiErrorOptions {
  fallback?: Partial<FriendlyError>;
  user?: CurrentUserSnapshot | null;
  action?: ToastActionElement;
  duration?: number;
  context?: Record<string, any>;
  report?: boolean;
}

export function showApiError(
  input: ApiErrorLike | Error | string | null | undefined,
  options: ShowApiErrorOptions = {},
): FriendlyError & { raw: string | null } {
  const { fallback, action, duration } = options;
  const user = options.user !== undefined ? options.user : getCurrentUser();
  // The current page is a fallback hint for which integration to link to
  // (an error on /home/shopify that doesn't name Shopify → "Open Shopify").
  const page = typeof window !== "undefined" ? window.location.pathname : undefined;
  const friendly = resolveFriendlyError(input, fallback, audienceOf(user), page);
  const button = action ?? catalogActionButton(friendly.action);

  toast({
    title: friendly.title,
    description: friendly.description,
    variant: variantForSeverity(friendly.severity),
    ...(button ? { action: button } : {}),
    ...(duration ? { duration } : {}),
  });

  return friendly;
}
